import { memo, useCallback, useMemo, useState } from "react";

import { useTranslate } from "@/hooks/use-translate/useTranslate";
import Label from "../label/Label";
import { Icon } from "../icons/Icon";
import { cn } from "@/lib/styles.utils";
import type { IconName } from "../icons/icons";

type BaseInputTextProps = {
  placeholder: string;
  value: string;
  size: "large" | "medium";
  label?: string;
  message?: string;
  disabled?: boolean;
  icon?: IconName;
  name?: string;
  width?: string;
  noBottomSpace?: boolean;
  required?: boolean;
  requiredAsterisk?: boolean;
  warning?: boolean;
  warningMessage?: string;
  error?: boolean;
  errorMessage?: string; // ← fix typo
  success?: boolean;
  successMessage?: string;
  helpText?: string;
  autoClean?: boolean;
};

type ReadOnlyProps = BaseInputTextProps & {
  readOnly: true;
  onChange?: (data: string) => void;
};

type EditableProps = BaseInputTextProps & {
  readOnly?: false;
  onChange: (data: string) => void;
};

type InputTextProps = ReadOnlyProps | EditableProps;

const InputText = ({
  label,
  placeholder,
  value,
  size,
  onChange,
  disabled,
  readOnly,
  required,
  warning,
  warningMessage,
  error,
  errorMessage,
  success,
  successMessage,
  helpText = "",
  autoClean = true,
  icon,
  name,
  width,
  noBottomSpace,
  requiredAsterisk,
}: InputTextProps) => {
  const translate = useTranslate();

  const [showHelpText, setShowHelpText] = useState(false);

  const changeValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!readOnly) {
        const newValue = event.target.value;
        onChange(newValue);
      }
    },
    [onChange, readOnly],
  );

  const identificator = useMemo(() => {
    if (name) return name;
    if (!(label || placeholder)) return "textinput";
    const _formatIdentificator = (text: string): string =>
      text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replaceAll(" ", "-")
        .toLocaleLowerCase();
    return label
      ? _formatIdentificator(label)
      : _formatIdentificator(placeholder);
  }, [name, label, placeholder]);

  const requiredAlert = useMemo(
    () => required && !(value.trim().length > 0),
    [required, value],
  );

  const widthClass = useMemo(
    () => (width ? { width: width } : { width: "100%" }),
    [width],
  );

  const inputClasses = useMemo(() => {
    const _getWidtAnBorders = () => {
      let classes = "focus:outline-none rounded-[2px] border ";
      if (error || requiredAlert) {
        classes += "border-ib-red-main";
      } else if (warning) {
        classes += "border-ib-orange-warning";
      } else if (success) {
        classes += "border-ib-green-succes";
      } else {
        classes += "border-ib-gray-borders";
      }
      if (!required && !readOnly) {
        classes += " hover:border-ib-red-lightest-2 focus:border-ib-red-main";
      }
      classes +=
        " disabled:border-ib-gray-borders read-only:border-ib-gray-borders";

      return classes;
    };

    const _getPadding = () => {
      let classes = size === "medium" ? " py-[9px]" : " py-[13px]";
      if ((!autoClean || disabled || readOnly) && !icon) {
        classes += " px-3";
        return classes;
      }
      if (icon && (!autoClean || disabled || readOnly)) {
        classes += " pl-9 pr-3";
        return classes;
      }
      if (icon && autoClean) {
        classes += " px-9";
        return classes;
      }
      classes += " pl-3 pr-9";
      return classes;
    };

    const borders = _getWidtAnBorders();
    const paddings = _getPadding();
    const generic =
      "w-full bg-ib-ib-white read-only:bg-ib-gray-lightest mb-2 text-ib-main read-only:text-ib-gray-darkest";
    const placeholder = disabled
      ? "playholder:text-ib-gray-dark-2"
      : "playholder:ib-gray-disabled";
    return `${generic} ${borders} ${paddings} ${placeholder}`;
  }, [
    disabled,
    error,
    requiredAlert,
    warning,
    success,
    required,
    readOnly,
    size,
    autoClean,
    icon,
  ]);

  const infoMessage = useMemo(() => {
    if (requiredAlert) return translate("required");
    if (warning) {
      if (warningMessage) return warningMessage;
      return helpText;
    }
    if (error) {
      if (errorMessage) return errorMessage;
      return helpText;
    }
    if (success) {
      if (successMessage) return successMessage;
      return helpText;
    }
    if (helpText && (showHelpText || disabled || readOnly)) {
      return helpText;
    }
    return null;
  }, [
    requiredAlert,
    translate,
    warning,
    error,
    success,
    helpText,
    showHelpText,
    disabled,
    readOnly,
    warningMessage,
    errorMessage,
    successMessage,
  ]);

  const helpClasses = useMemo(() => {
    const helpTextClass = "text-[12px] leading-[20px]";
    if (error || requiredAlert) {
      return {
        icon: "text-ib-red-main",
        text: `text-ib-red-main ${helpTextClass}`,
      };
    }
    if (warning) {
      return {
        icon: "text-ib-yellow-warning-darkest",
        text: `text-ib-orange-warning-dark ${helpTextClass}`,
      };
    }
    if (success) {
      return {
        icon: "text-ib-green-success-dark",
        text: `text-ib-green-success-dark ${helpTextClass}`,
      };
    }
    return {
      icon: "text-ib-gray-dark-2",
      text: `text-ib-gray-dark-2 ${helpTextClass}`,
    };
  }, [error, warning, success, requiredAlert]);

  const showOrHideHelpText = useCallback(() => {
    const newShowState = !showHelpText;
    setShowHelpText(newShowState);
  }, [showHelpText]);

  const iconsPostion = useMemo(() => {
    if (size === "medium") {
      return {
        autoClose: { transform: "translateY(37px)" },
        leftIcon: {
          transform: noBottomSpace ? "translateY(-36px)" : "translateY(-56px)",
        },
      };
    }
    return {
      autoClose: { transform: "translateY(42px)" },
      leftIcon: {
        transform: noBottomSpace ? "translateY(-40px)" : "translateY(-60px)",
      },
    };
  }, [size]);

  return (
    <div
      className={`${
        !noBottomSpace && label
          ? "h-[98px] min-h-[98px] max-h-[98px]"
          : !label
            ? "h-[41px] min-h-[41px] max-h-[41px]"
            : "h-[65px] min-h-[65px] max-h-[65px]"
      }`}
      style={widthClass}
    >
      {label ? (
        <div className="flex flex-row justify-between gap-x-2 items-center mb-2">
          <div className="flex flex-row gap-x-2 items-center">
            {
              <Label
                label={label}
                id={identificator}
                required={required || requiredAsterisk}
              />
            }
            {helpText ? (
              <button type="button" onClick={showOrHideHelpText}>
                <Icon name="info" className="text-ib-gray-dark-2 size-3" />
              </button>
            ) : null}
          </div>
          {autoClean && !disabled && !readOnly ? (
            <button
              type="button"
              className="h-4 w-4 flex justify-center items-center mr-3"
              style={iconsPostion.autoClose}
              onClick={() => {
                onChange("");
              }}
            >
              <Icon name="closeCircle" className="text-ib-red-main size-3" />
            </button>
          ) : null}
        </div>
      ) : null}
      <input
        type="text"
        id={identificator}
        name={identificator}
        disabled={disabled}
        readOnly={readOnly}
        required={requiredAlert}
        className={inputClasses}
        placeholder={placeholder}
        onChange={changeValue}
        value={value}
      ></input>
      {!noBottomSpace ? (
        <div
          className={`h-5 flex flex-row gap-x-2 items-center ${helpClasses.icon}`}
        >
          {infoMessage ? (
            <>
              <Icon name="info" className="size-4" height="16px" />
              <div className={helpClasses.text}>{infoMessage}</div>
            </>
          ) : null}
        </div>
      ) : null}
      {icon ? (
        <div
          style={iconsPostion.leftIcon}
          className="ml-3 w-fit pointer-events-none "
        >
          <Icon
            name={icon}
            className={cn(
              "size-4",
              disabled || readOnly
                ? "text-ib-gray-disabled"
                : "text-ib-red-main",
            )}
          />
        </div>
      ) : null}
    </div>
  );
};

export default memo(InputText);
